import { Controller } from "@hotwired/stimulus"

/**
 * 오디오 파일을 재생하고 UI를 제어하는 Stimulus 컨트롤러입니다.
 * 기능: 재생/일시정지, 탐색, 시간 표시, 진행률 표시, 키보드 컨트롤.
 * 페이지 내 다른 오디오 플레이어가 재생될 때 현재 플레이어를 자동으로 일시정지합니다.
 */
export default class extends Controller {
  static targets = ["audio", "toggleButton", "playIcon", "pauseIcon", "currentTime", "progressBar", "timeline"]

  static values = {
    playLabel: { type: String, default: "Play" },
    pauseLabel: { type: String, default: "Pause" },
  }

  connect() {
    if (!this.hasAudioTarget) {
      console.warn("Audio target not found for audio-player-controller.")
      return
    }

    this._setupEventListeners()

    this._updateDurationUI()
    this._updateUI()
  }

  disconnect() {
    this._removeEventListeners()
  }

  // --- Actions ---

  toggle() {
    if (this.audioTarget.paused || this.audioTarget.ended) {
      this.audioTarget.play().catch(e => console.error("Audio play failed:", e))
    } else {
      this.audioTarget.pause()
    }
  }

  seek(event) {
    if (!this.hasTimelineTarget || !this._isAudioReady()) return

    const rect = this.timelineTarget.getBoundingClientRect()
    if (rect.width === 0) return

    const clickPosition = event.clientX - rect.left
    const ratio = Math.min(Math.max(clickPosition / rect.width, 0), 1)
    this.audioTarget.currentTime = ratio * this.audioTarget.duration
  }

  // --- Event Handlers ---

  _onPlay() {
    this._updateUI()
    // 다른 오디오 플레이어를 일시정지시키기 위한 전역 이벤트 발행
    window.dispatchEvent(new CustomEvent("audio-player:play", { detail: { controller: this } }))
  }

  _onPause() {
    this._updateUI()
  }

  _onEnded() {
    this.audioTarget.currentTime = 0
    this._updateUI()
  }

  _onTimeUpdate() {
    this._updateProgressUI()
  }

  _onLoadedMetadata() {
    this._updateDurationUI()
    this._updateUI()
  }

  _handleExternalPlay(event) {
    if (event.detail?.controller !== this) {
      this.audioTarget.pause()
    }
  }

  // --- Private UI Updaters ---

  _updateUI() {
    this._updateButtonAndIcons()
    this._updateProgressUI()
  }

  _updateProgressUI() {
    const isReady = this._isAudioReady()
    const currentTime = this.audioTarget.currentTime || 0
    const duration = this.audioTarget.duration
    const percentage = isReady ? (currentTime / duration) * 100 : 0
    const isPlaying = !this.audioTarget.paused && !this.audioTarget.ended

    if (this.hasCurrentTimeTarget) {
      if (isPlaying) this.currentTimeTarget.textContent = this._formatTime(currentTime)
      else this.currentTimeTarget.textContent = isReady ? this._formatTime(duration) : "--:--"
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }

    if (this.hasTimelineTarget) {
      this.timelineTarget.setAttribute("aria-valuenow", Math.round(percentage))
      this.timelineTarget.setAttribute("aria-valuetext", `Current time: ${this._formatTime(currentTime)}`)
    }
  }

  _updateDurationUI() {
    const { duration } = this.audioTarget
    const formattedDuration = this._isAudioReady() ? this._formatTime(duration) : "--:--"

    if (this.hasTimelineTarget) {
      this.timelineTarget.setAttribute("aria-valuemax", duration || 0)
      this.timelineTarget.setAttribute("aria-valuetext", `Total duration: ${formattedDuration}`)
    }
  }

  _updateButtonAndIcons() {
    const isPlaying = !this.audioTarget.paused && !this.audioTarget.ended

    if (this.hasPlayIconTarget) this.playIconTarget.classList.toggle("hidden", isPlaying)
    if (this.hasPauseIconTarget) this.pauseIconTarget.classList.toggle("hidden", !isPlaying)

    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.setAttribute("aria-pressed", String(isPlaying))
      this.toggleButtonTarget.setAttribute("aria-label", isPlaying ? this.pauseLabelValue : this.playLabelValue)
    }
  }

  // --- Utilities ---

  _setupEventListeners() {
    this.bound = {
      onPlay: this._onPlay.bind(this),
      onPause: this._onPause.bind(this),
      onEnded: this._onEnded.bind(this),
      onTimeUpdate: this._onTimeUpdate.bind(this),
      onLoadedMetadata: this._onLoadedMetadata.bind(this),
      handleExternalPlay: this._handleExternalPlay.bind(this),
    }

    this.audioTarget.addEventListener("play", this.bound.onPlay)
    this.audioTarget.addEventListener("pause", this.bound.onPause)
    this.audioTarget.addEventListener("ended", this.bound.onEnded)
    this.audioTarget.addEventListener("timeupdate", this.bound.onTimeUpdate)
    this.audioTarget.addEventListener("loadedmetadata", this.bound.onLoadedMetadata)
    window.addEventListener("audio-player:play", this.bound.handleExternalPlay)
  }

  _removeEventListeners() {
    if (!this.bound) return

    this.audioTarget.removeEventListener("play", this.bound.onPlay)
    this.audioTarget.removeEventListener("pause", this.bound.onPause)
    this.audioTarget.removeEventListener("ended", this.bound.onEnded)
    this.audioTarget.removeEventListener("timeupdate", this.bound.onTimeUpdate)
    this.audioTarget.removeEventListener("loadedmetadata", this.bound.onLoadedMetadata)
    window.removeEventListener("audio-player:play", this.bound.handleExternalPlay)
  }

  _isAudioReady() {
    const { duration } = this.audioTarget
    return duration > 0 && Number.isFinite(duration)
  }

  _formatTime(timeInSeconds) {
    const totalSeconds = Math.floor(Math.max(0, timeInSeconds))
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  }
}
